// Мягкий валютный рубеж на денежных суммах.
//
// Повод — разбор 28.08.2026. На проде нашлись 25 строк со значениями, которых не
// бывает, и механизм у большинства оказался фронтовым: склейка строк. `bookings.total`
// брони 12527 разбирается ровно как "9000"+"4000"+"2000"+"0"+"0"+"0" — это оператор `+`
// над строками, а не переполнение. Плюс отдельный случай зажатой клавиши (пятнадцать
// девяток в сумме долга).
//
// Сервер с api#571 отбивает НЕВОЗМОЖНОЕ (NaN, бесконечности, всё выше 1e12) — там отказ
// уместен, ложных срабатываний нет по построению. Но между «невозможным» и настоящей
// крупной суммой в слабой валюте зазор всего ×6: законный максимум прода — 369 376 663
// UZS, а найденный мусор начинается с 2 345 767 809. Жёсткий отказ на таком зазоре рано
// или поздно отобьёт настоящий перевод в узбекских суммах. Поэтому здесь НЕ отказ, а
// вопрос оператору.
//
// ── ОТКУДА ПОРОГИ ──────────────────────────────────────────────────────────────
//
// Замер по проду 28.08.2026: максимум |sum| в `counts` по каждой валюте с объёмом
// больше 20 строк, мусорный эшелон исключён. Порог — ×10 от наблюдаемого максимума.
//
// Множитель именно такой, потому что дефект этого класса промахивается на ПОРЯДКИ, а
// не в разы: склейка двух чисел даёт ×10^5…10^9, зажатая клавиша — ещё больше. ×10
// ловит их с запасом и при этом не дёргает оператора на законной крупной операции.
//
// Валюты вне таблицы — низкообъёмные (десятки строк на всю базу); им дан
// снисходительный дефолт: лучше промолчать на редкой валюте, чем ежедневно спрашивать
// впустую и приучить нажимать «да» не глядя.
export const CURRENCY_SOFT_LIMITS = Object.freeze({
  UZS: 4e9, // максимум прода 369 376 663
  VND: 2.2e9, // 221 512 900
  KZT: 1.5e9, // 141 612 437
  IDR: 3e8, // 30 000 000
  THB: 2e8, // 19 170 870
  EUR: 1.6e8, // 16 000 000
  RUB: 1e8, // p9999 7 715 438 (максимум зашумлён мусором)
  BYN: 1.5e7, // 1 446 418
  RSD: 1.4e7, // 1 386 450
  GEL: 1.1e7, // 1 067 000
  USD: 2.5e6, // 243 548
  INR: 2.3e6, // 225 000
  ALL: 2.2e6, // 215 990
  AED: 2e6, // 200 000
  MDL: 5e5, // 50 000
  TRY: 2.6e5, // 25 200
  UAH: 2.6e5, // 25 200
  PLN: 2e5, // 19 800
  RON: 2e5, // 19 800
});

export const DEFAULT_SOFT_LIMIT = 1e9;

// Ключи денежных полей в исходящих телах запросов. Список закрытый намеренно: скан
// «всего, что похоже на число» дёргал бы оператора на пробеге, координатах и id.
export const MONEY_KEYS = Object.freeze([
  "sum",
  "total",
  "amount",
  "price",
  "deposit",
  "balance",
  "cash",
  "cashless",
  "rental_cost",
  "new_price",
]);

export function softLimitFor(currencyCode) {
  if (!currencyCode) return DEFAULT_SOFT_LIMIT;
  const limit = CURRENCY_SOFT_LIMITS[String(currencyCode).toUpperCase()];
  return typeof limit === "number" ? limit : DEFAULT_SOFT_LIMIT;
}

// Предел глубины обхода. Замер 29.08.2026: рекурсия падает Maximum call stack между
// 2000 и 4000 уровнями, а реальные тела приложения — меньше десятка. Практически порог
// недостижим, но обход живёт в интерцепторе на КАЖДОМ исходящем запросе, и падение
// там уронило бы запрос целиком. 64 — на порядок выше любого настоящего тела и на два
// порядка ниже границы стека; глубже мы просто не ищем.
export const MAX_SCAN_DEPTH = 64;

function codeById(id, currencies) {
  if (!id && id !== 0) return null;
  const found = (currencies || []).find((c) => String(c.id) === String(id));
  return (found && found.code) || null;
}

// Обход вглубь. Валюта резолвится ПОУЗЛОВО, а не один раз на всё тело — это не
// педантизм, а исправление дефекта, найденного ревью 29.08.2026.
//
// Прежняя редакция брала первый попавшийся `currency_id` и применяла его лимит ко
// всем суммам. В самом денежном теле приложения — приёмка брони `POST in_home` — 14+
// платежей, у КАЖДОГО свой `sum` и свой `currency_id` (мультикасса). Замер на реальной
// форме тела показал обе стороны отказа:
//
//   мусор 2 345 767 809 RUB рядом с мойкой в UZS → резолвился UZS (лимит 4e9) →
//     ровно то значение, ради которого рубеж и делался, проходило МОЛЧА;
//   законные 369 376 663 UZS рядом с платежом в RUB → резолвился RUB (лимит 1e8) →
//     ложный вопрос на максимуме прода.
//
// Теперь каждый узел наследует ближайший объемлющий `currency_id`, а свой — перебивает.
export function findLargeAmount(body, options = {}) {
  const currencies = options.currencies || [];
  const baseCode = options.baseCode || null;
  const seen = new Set();

  const walk = (node, depth, inheritedCode) => {
    if (depth > MAX_SCAN_DEPTH) return null;
    if (!node || typeof node !== "object") return null;
    if (seen.has(node)) return null; // защита от циклов
    seen.add(node);

    if (Array.isArray(node)) {
      for (const item of node) {
        const hit = walk(item, depth + 1, inheritedCode);
        if (hit) return hit;
      }
      return null;
    }

    // Своя валюта узла перебивает унаследованную.
    const ownCode = codeById(node.currency_id, currencies);
    const code = ownCode || inheritedCode;
    const limit = softLimitFor(code);

    for (const [key, value] of Object.entries(node)) {
      if (value && typeof value === "object") {
        const hit = walk(value, depth + 1, code);
        if (hit) return hit;
        continue;
      }
      if (!MONEY_KEYS.includes(key)) continue;
      // Строку тоже приводим: v-model без .number отдаёт строку, и именно строковая
      // арифметика во фронте породила испорченные total. Отформатированная строка
      // («1 000 000») даёт NaN и пропускается — названное слепое пятно: исходный
      // дефект даёт чистую цепочку цифр, она ловится.
      const amount = typeof value === "string" ? Number(value.trim()) : value;
      if (typeof amount !== "number" || !Number.isFinite(amount)) continue;
      if (Math.abs(amount) > limit) return { key, value: amount, limit, currency: code };
    }
    return null;
  };

  return walk(body, 0, baseCode);
}

// Базовая валюта компании — фолбэк для узлов, у которых своего `currency_id` нет.
export function baseCurrencyCode(company) {
  return (company && company.company && company.company.currency) || null;
}

export function companyCurrencies(company) {
  return (company && company.company && company.company.currencies) || [];
}
